import { type IHeaderParams } from "ag-grid-community";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { type FC, type MouseEvent, useCallback, useMemo, useRef } from "react";
import type { PressEvent } from "react-aria";
import { HStack, TriggerButton, TriggerButtonSize } from "widgets";
import { AnimatedTooltip } from "widgets";
import { useTooltip } from "widgets/hooks";

import { isProfileFilterColumn } from "@/app-utils/filter.ts";
import { type IRsqlComparisonExpr, parse, SEARCH_OPERATORS } from "@/app-utils/rsql.ts";
import { type SortData } from "@/graphql.ts";
import { getCellExpansionKey, type ITableColumn } from "@/redux/modules/table-data.ts";
import { CELL_EXPANSION, SORT_ORDER_ASC, SORT_ORDER_DESC } from "@/settings/settings.ts";
import { ExpansionIconCell } from "@/ui-modules/core/grid/cells/headers/ExpansionIconCell.tsx";
import type { IColumnMenuData } from "@/ui-modules/core/grid/ColumnMenu.tsx";
import { type ICustomColDef } from "@/ui-modules/core/grid/DataTable.tsx";
import { SampleEditingMenuTrigger } from "@/ui-modules/playbooks/sample/SampleGridEditingMenu.tsx";

import style from "./HeaderCell.module.css";

export interface HeaderCellProps {
  hideMenu?: boolean;
  onClickEditButtonCallback?: (columnId: number) => void;
  wrapColumnHeaders?: boolean;

  onColumnSortClick?: (columnField: string, sortOrder: string, multiSort: boolean) => void;
  onRemoveFilterClick?: (columnField: string) => void;
  onFilter?: (columnField: string, filterValue: string) => void;
  onMenuButtonClicked?: (columnMenuData: IColumnMenuData) => void;
  getGridColumns?: () => ITableColumn[];

  sampleEditingMenu?: boolean;
}

export const HeaderCell: FC<HeaderCellProps & IHeaderParams> = (props) => {
  const {
    column: gridColumn,
    getGridColumns,
    onRemoveFilterClick,
    onColumnSortClick,
    progressSort,
    onMenuButtonClicked,
    wrapColumnHeaders,
    onClickEditButtonCallback,
    hideMenu,
    sampleEditingMenu,
    api,
    context,
  } = props;

  const menuWrapperRef = useRef<HTMLDivElement>(null);

  const getTableColumn = useCallback(
    (columnDef: ICustomColDef): ITableColumn | undefined => {
      if (getGridColumns) {
        const columns: ITableColumn[] = getGridColumns();
        return columns?.find((col: ITableColumn) => col.field === columnDef.field);
      }
      return undefined;
    },
    [getGridColumns],
  );

  const handleSort = useCallback(
    (event: MouseEvent<HTMLDivElement> | PressEvent) => {
      if (!onColumnSortClick) {
        progressSort(event.shiftKey);
        return;
      }

      const multiSort: boolean = event.shiftKey;
      const columnDef: ICustomColDef = gridColumn.getColDef();
      const tableColumn: ITableColumn | undefined = getTableColumn(columnDef);
      const sort: SortData | undefined | null = tableColumn ? tableColumn.sort : columnDef.sorting;

      // Determine new sort order
      let newSortOrder: string | null;
      if (!sort) {
        newSortOrder = SORT_ORDER_ASC;
      } else if (sort.direction === SORT_ORDER_ASC) {
        newSortOrder = SORT_ORDER_DESC;
      } else if (sort.direction === SORT_ORDER_DESC) {
        newSortOrder = null;
      } else {
        newSortOrder = SORT_ORDER_ASC;
      }

      onColumnSortClick?.(columnDef.field!, newSortOrder!, multiSort);
    },
    [onColumnSortClick, progressSort, gridColumn, getTableColumn],
  );

  const handleRemoveFilter = useCallback(() => {
    const columnDef: ICustomColDef = gridColumn.getColDef();
    onRemoveFilterClick?.(columnDef.field!);
  }, [gridColumn, onRemoveFilterClick]);

  const handleMenuClick = useCallback(() => {
    const columnDef: ICustomColDef = gridColumn.getColDef();
    onMenuButtonClicked?.({
      activeColumn: getTableColumn(columnDef)!,
      menuTarget: menuWrapperRef,
    });
  }, [gridColumn, onMenuButtonClicked, getTableColumn]);

  // Computed values
  const columnDef: ICustomColDef = gridColumn.getColDef();
  const headerName = columnDef.headerName!;
  const tableColumn: ITableColumn | undefined = getTableColumn(columnDef);
  const sort: SortData | undefined | null = tableColumn ? tableColumn.sort : columnDef.sorting;

  const sortable: boolean = (() => {
    const sortableByColumnDefinition: boolean = tableColumn ? !!tableColumn.sortable : !!columnDef.sortable;
    return !(sortableByColumnDefinition === false);
  })();

  const hasFilter: boolean = (() => {
    if (tableColumn) {
      const parsedFilter = parse(tableColumn.filterExpression);
      if (parsedFilter != null && isProfileFilterColumn(tableColumn.field) && "operator" in parsedFilter) {
        return (parsedFilter as IRsqlComparisonExpr).operator !== SEARCH_OPERATORS.IN;
      }
      return !!tableColumn.filterExpression;
    }
    return !!columnDef.filter;
  })();

  const isMultiSortActive = getGridColumns
    ? getGridColumns().filter((c: ITableColumn) => !!c.sort).length > 1
    : (api.getColumns() ?? []).filter((col) => col.getSort()).length > 1;

  const multiSortPriority = sort?.direction && isMultiSortActive ? sort.priority || 1 : null;
  const tooltip: string = tableColumn?.groupName ? `${tableColumn.groupName} - ${headerName}` : headerName;

  // Get expandable field
  const expandableField: string | null = useMemo(() => {
    if (!context?.tableSpec) return null;
    const column: ICustomColDef | null = gridColumn.getUserProvidedColDef();
    if (
      column &&
      (column.cellExpansion === CELL_EXPANSION.STANDALONE || column.cellExpansion === CELL_EXPANSION.CONTEXT)
    ) {
      return getCellExpansionKey(column);
    }
    return null;
  }, [context?.tableSpec, gridColumn]);

  return (
    <HStack spacing={1} className={style.header_cell}>
      <HeaderLabel
        name={headerName}
        tooltip={tooltip}
        sortable={sortable}
        wrapText={!!wrapColumnHeaders}
        onSort={handleSort}
      />

      <HStack spacing={0} style={{ marginLeft: "auto" }}>
        {expandableField && context?.tableSpec && (
          <ExpansionIconCell tableQuerySpec={context.tableSpec} expandableField={expandableField} />
        )}

        {sort?.direction && (
          <SortIndicator direction={sort.direction} priority={multiSortPriority} onSort={handleSort} />
        )}

        {hasFilter && <FilterIndicator onRemove={handleRemoveFilter} />}

        {sampleEditingMenu && columnDef.editable && <SampleEditingMenuTrigger {...props} />}

        {onClickEditButtonCallback && columnDef.editable && (
          <EditButton headerName={headerName} columnId={Number(columnDef.colId)} onEdit={onClickEditButtonCallback} />
        )}

        {!hideMenu && <HeaderMenu columnDef={columnDef} onMenuClick={handleMenuClick} menuRef={menuWrapperRef} />}
      </HStack>
    </HStack>
  );
};

interface HeaderLabelProps {
  name: string;
  tooltip: string;
  sortable: boolean;
  wrapText: boolean;
  onSort?: (event: MouseEvent<HTMLDivElement>) => void;
}

const HeaderLabel: FC<HeaderLabelProps> = ({ name, tooltip, sortable, wrapText, onSort }) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef);

  const className = clsx(style.header_title, {
    [style.clickable_header_cell]: sortable,
    [style.ellipsed]: !wrapText,
    [style.wrapped]: wrapText,
  });

  return (
    <>
      <div ref={triggerRef} className={className} onClick={sortable ? onSort : undefined} {...triggerProps}>
        {name}
      </div>
      <AnimatedTooltip targetRef={triggerRef} state={state} domProps={tooltipProps} nonInteractive>
        {tooltip}
      </AnimatedTooltip>
    </>
  );
};

interface SortIndicatorProps {
  direction: string;
  priority?: number | null;
  onSort: (event: PressEvent) => void;
}

const SortIndicator: FC<SortIndicatorProps> = ({ direction, priority, onSort }) => {
  let sortIcon: MaterialSymbol;
  let sortDirection: string;

  if (direction.toLowerCase() === SORT_ORDER_ASC.toLowerCase()) {
    sortIcon = "arrow_upward";
    sortDirection = "up";
  } else {
    sortIcon = "arrow_downward";
    sortDirection = "down";
  }

  return (
    <TriggerButton
      icon={sortIcon}
      label={"Sort"}
      size={TriggerButtonSize.Mini}
      onPress={onSort}
      badge={priority || undefined}
      data-sort={sortDirection}
    />
  );
};

interface FilterIndicatorProps {
  onRemove: () => void;
}

const FilterIndicator: FC<FilterIndicatorProps> = ({ onRemove }) => (
  <TriggerButton icon={"filter_alt_off"} label={"Remove Filter"} size={TriggerButtonSize.Mini} onPress={onRemove} />
);

interface HeaderMenuProps {
  columnDef: ICustomColDef;
  onMenuClick: () => void;
  menuRef: React.RefObject<HTMLDivElement | null>;
}

const HeaderMenu: FC<HeaderMenuProps> = ({ onMenuClick, menuRef }) => (
  <div ref={menuRef}>
    <span id="table-page-column-tools" className="hotspot-beacon" />
    <TriggerButton
      icon={"more_vert"}
      aria-label={"Column Actions"}
      label=""
      size={TriggerButtonSize.Mini}
      onPress={onMenuClick}
    />
  </div>
);

interface EditButtonProps {
  headerName: string;
  columnId: number;
  onEdit: (columnId: number) => void;
}

const EditButton: FC<EditButtonProps> = ({ headerName, columnId, onEdit }) => (
  <TriggerButton
    tooltip={`Edit ${headerName} settings`}
    icon={"edit"}
    label={"Menu"}
    size={TriggerButtonSize.Mini}
    onPress={() => onEdit(columnId)}
  />
);
